/**
 * Taiwan (TW) invoice settings configuration
 *
 * Taiwan requires e-invoices (電子發票) for all B2B and B2C transactions.
 * Supports multiple carrier types for invoice storage/delivery:
 *
 * - none: 會員載具 (stored in platform, can be printed later)
 * - company: 公司發票 (B2B invoice with 統一編號)
 * - mobile_barcode: 手機條碼載具 (synced to government platform)
 * - citizen_certificate: 自然人憑證載具
 * - donate: 捐贈發票 (donated to charity with 愛心碼)
 */

export default {
  // Whether this country has special invoice settings
  hasInvoiceSettings: true,

  // Whether e-invoice is required by law
  eInvoiceRequired: true,

  // Available carrier type options
  carrierTypeOptions: [
    {
      value: 'none',
      label: '會員載具',
      labelEn: 'Platform Carrier',
      description: '發票存在平台，可隨時查詢列印',
      descriptionEn: 'Invoice stored in platform, can be queried and printed anytime',
    },
    {
      value: 'company',
      label: '公司發票',
      labelEn: 'Company Invoice',
      description: '需填寫統一編號，開立三聯式發票',
      descriptionEn: 'Requires Tax ID, issues triplicate invoice',
    },
    {
      value: 'mobile_barcode',
      label: '手機條碼',
      labelEn: 'Mobile Barcode',
      description: '發票歸戶至您的手機條碼載具',
      descriptionEn: 'Invoice synced to your mobile barcode carrier',
    },
    {
      value: 'citizen_certificate',
      label: '自然人憑證',
      labelEn: 'Citizen Certificate',
      description: '發票歸戶至自然人憑證',
      descriptionEn: 'Invoice synced to citizen certificate',
    },
    {
      value: 'donate',
      label: '捐贈發票',
      labelEn: 'Donate Invoice',
      description: '將發票捐贈給指定公益團體',
      descriptionEn: 'Donate invoice to specified charity',
    },
  ],

  // Invoice field configurations
  fields: [
    {
      name: 'tax_id',
      label: '統一編號',
      labelEn: 'Tax ID',
      type: 'text',
      placeholder: '請輸入 8 位統一編號',
      placeholderEn: 'Enter 8-digit Tax ID',
      maxLength: 8,
      requiredFor: ['company'],
      hint: '開立公司發票請填寫統一編號',
      hintEn: 'Required for company invoices',
    },
    {
      name: 'carrier_id',
      label: '載具條碼',
      labelEn: 'Carrier Barcode',
      type: 'text',
      placeholder: '請輸入載具條碼',
      placeholderEn: 'Enter carrier barcode',
      requiredFor: ['mobile_barcode', 'citizen_certificate'],
      // Dynamic labels based on carrier type
      dynamicLabel: {
        mobile_barcode: { zh: '手機條碼', en: 'Mobile Barcode' },
        citizen_certificate: { zh: '自然人憑證', en: 'Citizen Certificate' },
      },
      dynamicPlaceholder: {
        mobile_barcode: { zh: '請輸入手機條碼（含斜線）', en: 'Enter mobile barcode (with slash)' },
        citizen_certificate: { zh: '請輸入自然人憑證條碼', en: 'Enter citizen certificate barcode' },
      },
      dynamicHint: {
        mobile_barcode: { zh: '格式範例：/ABC1234', en: 'Example: /ABC1234' },
        citizen_certificate: { zh: '請輸入自然人憑證條碼', en: 'Enter citizen certificate barcode' },
      },
    },
    {
      name: 'love_code',
      label: '捐贈碼',
      labelEn: 'Love Code',
      type: 'text',
      placeholder: '請輸入受贈單位捐贈碼',
      placeholderEn: 'Enter charity donation code',
      maxLength: 7,
      requiredFor: ['donate'],
      hint: '請輸入 3-7 位數字的愛心碼',
      hintEn: 'Enter 3-7 digit love code',
    },
  ],

  // Validation rules
  validations: {
    tax_id: {
      requiredFor: ['company'],
      requiredMessage: '公司發票需填寫統一編號',
      pattern: /^\d{8}$/,
      patternMessage: '統一編號須為 8 位數字',
      customValidator: 'validateTaiwanTaxId',
    },
    carrier_id: {
      requiredFor: ['mobile_barcode', 'citizen_certificate'],
      requiredMessage: '請輸入載具條碼',
      patternFor: {
        mobile_barcode: /^\/[0-9A-Z.+-]{7}$/,
      },
      patternMessageFor: {
        mobile_barcode: '手機條碼格式不正確，須為斜線開頭加 7 位英數字',
      },
    },
    love_code: {
      requiredFor: ['donate'],
      requiredMessage: '捐贈發票需填寫捐贈碼',
      pattern: /^\d{3,7}$/,
      patternMessage: '捐贈碼須為 3-7 位數字',
    },
  },

  // UI configuration
  ui: {
    showInvoiceSection: true,
    showCarrierTypeSelector: true,
    defaultCarrierType: 'none',
  },

  /**
   * Validate Taiwan Tax ID (統一編號) checksum
   *
   * @param {string} taxId - 8-digit Taiwan business ID
   * @returns {{ valid: boolean, error?: string }}
   */
  validateTaiwanTaxId: (taxId) => {
    if (!taxId || !/^\d{8}$/.test(taxId)) {
      return { valid: false, error: '統一編號須為 8 位數字' };
    }

    // Taiwan Business ID checksum algorithm
    const weights = [1, 2, 1, 2, 1, 2, 4, 1];
    let sum = 0;

    for (let i = 0; i < 8; i++) {
      const product = parseInt(taxId[i], 10) * weights[i];
      sum += Math.floor(product / 10) + (product % 10);
    }

    // Special case for 7th digit being 7
    const valid = taxId[6] === '7'
      ? (sum % 10 === 0) || ((sum + 1) % 10 === 0)
      : (sum % 10 === 0);

    return valid
      ? { valid: true }
      : { valid: false, error: '統一編號驗證碼不正確' };
  },

  /**
   * Get field config with dynamic values based on carrier type
   *
   * @param {string} fieldName - Field name
   * @param {string} carrierType - Current carrier type
   * @param {string} locale - 'zh' or 'en'
   * @returns {Object} Field configuration
   */
  getFieldConfig: (fieldName, carrierType, locale = 'zh') => {
    const config = this.fields?.find(f => f.name === fieldName);
    if (!config) return null;

    const result = { ...config };

    // Apply dynamic values if available
    if (config.dynamicLabel?.[carrierType]) {
      result.label = config.dynamicLabel[carrierType][locale] || config.label;
    }
    if (config.dynamicPlaceholder?.[carrierType]) {
      result.placeholder = config.dynamicPlaceholder[carrierType][locale] || config.placeholder;
    }
    if (config.dynamicHint?.[carrierType]) {
      result.hint = config.dynamicHint[carrierType][locale] || config.hint;
    }

    return result;
  },

  /**
   * Check if a field is required for the given carrier type
   *
   * @param {string} fieldName - Field name
   * @param {string} carrierType - Current carrier type
   * @returns {boolean}
   */
  isFieldRequired: (fieldName, carrierType) => {
    const validation = this.validations?.[fieldName];
    if (!validation?.requiredFor) return false;
    return validation.requiredFor.includes(carrierType);
  },

  // Format display strings
  formatCarrierDisplay: (carrierType, { carrierId, taxId, loveCode } = {}) => {
    switch (carrierType) {
      case 'none':
        return '會員載具';
      case 'company':
        return taxId ? `公司發票 (統編: ${taxId})` : '公司發票';
      case 'mobile_barcode':
        return carrierId ? `手機條碼: ${carrierId}` : '手機條碼';
      case 'citizen_certificate':
        return carrierId ? `自然人憑證: ${carrierId}` : '自然人憑證';
      case 'donate':
        return loveCode ? `捐贈碼: ${loveCode}` : '捐贈發票';
      default:
        return '會員載具';
    }
  },

  formatTaxIdDisplay: (taxId) => {
    if (!taxId) return null;
    return `統一編號: ${taxId}`;
  },

  /**
   * Format e-invoice number for display
   * Taiwan format: "GL41000063" → "GL-41000063"
   *
   * @param {string} num - Raw e-invoice number
   * @returns {string} Formatted number
   */
  formatEInvoiceNumber: (num) => {
    if (!num || num.length !== 10) return num;
    return `${num.slice(0, 2)}-${num.slice(2)}`;
  },
};
